/**
 * BudgetsTab — Presupuestos y Variacion
 */
import { formatMoneyForTenant } from '@/lib/locale'
import { useState, useEffect } from 'react'
import { EntityAutocomplete } from '@/components/EntityAutocomplete'
import { Plus, Loader2, Trash2, PieChart, TrendingUp, TrendingDown } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import {
  useBudgets, useCreateBudget, useDeleteBudget, useBudgetVariance,
  useAccounts, useCostCenters,
  type BudgetItem, type BudgetVarianceItem, type ChartOfAccount,
} from '../hooks/useAccounting'
import { EmptyState } from '@/components/EmptyState'
import { Modal } from '@/components/ds/Modal'

const MONTHS = [
  { value: '', label: 'Todos los meses' },
  { value: '1', label: 'Enero' },
  { value: '2', label: 'Febrero' },
  { value: '3', label: 'Marzo' },
  { value: '4', label: 'Abril' },
  { value: '5', label: 'Mayo' },
  { value: '6', label: 'Junio' },
  { value: '7', label: 'Julio' },
  { value: '8', label: 'Agosto' },
  { value: '9', label: 'Septiembre' },
  { value: '10', label: 'Octubre' },
  { value: '11', label: 'Noviembre' },
  { value: '12', label: 'Diciembre' },
]

const MONTH_LABELS: Record<number, string> = {
  1: 'Enero', 2: 'Febrero', 3: 'Marzo', 4: 'Abril',
  5: 'Mayo', 6: 'Junio', 7: 'Julio', 8: 'Agosto',
  9: 'Septiembre', 10: 'Octubre', 11: 'Noviembre', 12: 'Diciembre',
}

function fmtCLP(value: string | number | undefined) {
  if (value === undefined || value === null) return '--'
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return '--'
  return formatMoneyForTenant(num)
}

// ─── Create Modal ───────────────────────────────────────────────────────────

interface BudgetFormProps {
  accounts: ChartOfAccount[]
  costCenters: { id: string; code: string; name: string; is_active: boolean }[]
  onClose: () => void
}

function BudgetForm({ accounts, costCenters, onClose }: BudgetFormProps) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const currentYear = new Date().getFullYear()
  const [name, setName] = useState('')
  const [year, setYear] = useState(currentYear)
  const [month, setMonth] = useState<string>('')
  const [account, setAccount] = useState('')
  const [costCenter, setCostCenter] = useState('')
  const [budgetedAmount, setBudgetedAmount] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)

  const create = useCreateBudget()

  const imputableAccounts = accounts.filter(a => a.is_imputable && a.is_active)
  const activeCenters = costCenters.filter(c => c.is_active)

  const handleSave = () => {
    if (!name || !account || !budgetedAmount) return
    setError(null)
    create.mutate(
      {
        name,
        year,
        month: month ? parseInt(month) : null,
        account,
        cost_center: costCenter || null,
        budgeted_amount: budgetedAmount,
        notes,
      },
      {
        onSuccess: onClose,
        onError: (e: unknown) => {
          const err = e as { response?: { data?: { error?: { message?: string } } } }
          setError(err.response?.data?.error?.message ?? 'Error al guardar')
        },
      },
    )
  }

  return (
    <Modal open onClose={onClose} title="Nuevo Presupuesto" size="2xl"
      actions={<>
        <button onClick={onClose} className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft">
          Cancelar
        </button>
        <button
          onClick={handleSave}
          disabled={create.isPending || !name || !account || !budgetedAmount}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-50"
        >
          {create.isPending && <Loader2 size={14} className="animate-spin" />}
          Crear Presupuesto
        </button>
      </>}
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="budgetstab-nombre" className="block text-sm font-medium text-content mb-1">Nombre *</label>
          <input id="budgetstab-nombre" value={name} onChange={e => setName(e.target.value)} placeholder="ej. Presupuesto Operacional 2026"
            className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="budgetstab-ano" className="block text-sm font-medium text-content mb-1">Ano *</label>
            <select id="budgetstab-ano" value={year} onChange={e => setYear(parseInt(e.target.value))}
              className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200">
              {[currentYear - 1, currentYear, currentYear + 1, currentYear + 2].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="budgetstab-mes" className="block text-sm font-medium text-content mb-1">Mes</label>
            <select id="budgetstab-mes" value={month} onChange={e => setMonth(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200">
              <option value="">Anual (sin mes)</option>
              {MONTHS.filter(m => m.value !== '').map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="budgetstab-cuenta-contable" className="block text-sm font-medium text-content mb-1">Cuenta Contable *</label>
            <EntityAutocomplete id="budgetstab-cuenta-contable"
              options={imputableAccounts.map(a => ({ id: a.id, label: `${a.code} — ${a.name}` }))}
              value={account}
              onChange={setAccount}
              placeholder="Seleccionar cuenta"
              required
            />
          </div>
          <div>
            <label htmlFor="budgetstab-centro-de-costo" className="block text-sm font-medium text-content mb-1">Centro de Costo</label>
            <EntityAutocomplete id="budgetstab-centro-de-costo"
              options={activeCenters.map(c => ({ id: c.id, label: `${c.code} — ${c.name}` }))}
              value={costCenter}
              onChange={setCostCenter}
              placeholder="Sin centro de costo"
              allowClear
              clearLabel="Sin centro de costo"
            />
          </div>
        </div>
        <div>
          <label htmlFor="budgetstab-monto-presupuestado" className="block text-sm font-medium text-content mb-1">Monto Presupuestado *</label>
          <input id="budgetstab-monto-presupuestado" type="number" value={budgetedAmount} onChange={e => setBudgetedAmount(e.target.value)} placeholder="0"
            className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200" />
        </div>
        <div>
          <label htmlFor="budgetstab-notas" className="block text-sm font-medium text-content mb-1">Notas</label>
          <textarea id="budgetstab-notas" value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Notas adicionales (opcional)"
            className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200 resize-none" />
        </div>
        {error && (
          <div className="bg-danger-surface border border-danger-border rounded-lg px-3 py-2 text-sm text-danger-content">{error}</div>
        )}
      </div>
    </Modal>
  )
}

// ─── Delete confirm ─────────────────────────────────────────────────────────

function BudgetDeleteButton({ budget }: { budget: BudgetItem }) {
  const [confirm, setConfirm] = useState(false)
  const deleteMut = useDeleteBudget()

  if (confirm) {
    return (
      <div className="flex items-center gap-1">
        <span className="text-xs text-danger-content-soft mr-1">Eliminar?</span>
        <button
          onClick={() => deleteMut.mutate(budget.id, { onSettled: () => setConfirm(false) })}
          disabled={deleteMut.isPending}
          className="px-2 py-1 text-xs text-white bg-danger-fill rounded hover:bg-danger-fill-hover disabled:opacity-50"
        >
          {deleteMut.isPending ? <Loader2 size={10} className="animate-spin" /> : 'Si'}
        </button>
        <button onClick={() => setConfirm(false)}
          className="px-2 py-1 text-xs text-content-muted border border-divider rounded hover:bg-soft">
          No
        </button>
      </div>
    )
  }

  return (
    <button
      onClick={() => setConfirm(true)}
      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-danger-content-soft border border-danger-border rounded-lg hover:bg-danger-surface"
      title="Eliminar presupuesto"
    >
      <Trash2 size={10} />Eliminar
    </button>
  )
}

// ─── Main Tab ───────────────────────────────────────────────────────────────

export function BudgetsTab() {
  const currentYear = new Date().getFullYear()
  const [filterYear, setFilterYear] = useState(currentYear)
  const [filterMonth, setFilterMonth] = useState<string>('')
  const [filterCC, setFilterCC] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [viewMode, setViewMode] = useState<'budgets' | 'variance'>('budgets')

  const budgetParams: Record<string, number | string> = { year: filterYear }
  if (filterMonth) budgetParams.month = parseInt(filterMonth)
  if (filterCC) budgetParams.cost_center_id = filterCC

  const { data: budgetsData, isLoading: loadingBudgets } = useBudgets(budgetParams as Parameters<typeof useBudgets>[0])
  const { data: varianceData, isLoading: loadingVariance } = useBudgetVariance(budgetParams as Parameters<typeof useBudgetVariance>[0])
  const { data: accountsData } = useAccounts({ is_active: 'true' })
  const { data: ccData } = useCostCenters()

  const budgets = budgetsData?.budgets ?? []
  const variance = varianceData?.variance ?? []
  const accounts = accountsData?.accounts ?? []
  const costCenters = ccData?.cost_centers ?? []

  return (
    <div>
      {/* Filters */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex gap-2 flex-wrap">
          <select aria-label="Año" value={filterYear} onChange={e => setFilterYear(parseInt(e.target.value))}
            className="border border-divider rounded-lg px-3 py-1.5 text-sm text-content focus:ring-2 focus:ring-primary">
            {[currentYear - 1, currentYear, currentYear + 1, currentYear + 2].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <select aria-label="Mes" value={filterMonth} onChange={e => setFilterMonth(e.target.value)}
            className="border border-divider rounded-lg px-3 py-1.5 text-sm text-content focus:ring-2 focus:ring-primary">
            {MONTHS.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          {costCenters.length > 0 && (
            <div className="w-56">
              <EntityAutocomplete aria-label="Centro de costo"
                options={costCenters.filter(c => c.is_active).map(c => ({ id: c.id, label: `${c.code} — ${c.name}` }))}
                value={filterCC}
                onChange={setFilterCC}
                placeholder="Todos los CC"
                allowClear
                clearLabel="Todos los CC"
              />
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <div className="flex bg-glass-3 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('budgets')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                viewMode === 'budgets' ? 'bg-surface text-content shadow-sm' : 'text-content-muted hover:text-content'
              }`}
            >
              Presupuestos
            </button>
            <button
              onClick={() => setViewMode('variance')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                viewMode === 'variance' ? 'bg-surface text-content shadow-sm' : 'text-content-muted hover:text-content'
              }`}
            >
              Variacion
            </button>
          </div>
          <button
            onClick={() => setModalOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary text-sm rounded-lg hover:bg-primary-hover"
          >
            <Plus size={14} />Nuevo Presupuesto
          </button>
        </div>
      </div>

      {/* Budgets Table */}
      {viewMode === 'budgets' && (
        <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-x-auto shadow-card">
          {loadingBudgets ? (
            <div className="flex items-center justify-center py-16 text-content-subtle">
              <Loader2 size={20} className="animate-spin mr-2" />Cargando...
            </div>
          ) : budgets.length === 0 ? (
            <div className="text-center py-16">
              <PieChart size={32} className="mx-auto text-content-subtle mb-3" />
              <p className="text-content-muted text-sm font-medium">Sin presupuestos configurados</p>
              <button
                onClick={() => setModalOpen(true)}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm text-primary border border-primary-200 rounded-lg hover:bg-primary-50"
              >
                <Plus size={14} />Crear primer presupuesto
              </button>
            </div>
          ) : (
            <DataTable
              columns={[
                { key: 'name', header: 'Nombre', render: b => <span className="font-medium text-content">{b.name}</span> },
                {
                  key: 'account_code', header: 'Cuenta',
                  render: b => (
                    <>
                      <span className="font-mono text-xs">{b.account_code}</span>
                      <span className="text-content-subtle ml-1 text-xs">{b.account_name}</span>
                    </>
                  ),
                },
                { key: 'cost_center_name', header: 'Centro Costo', render: b => b.cost_center_name || <span className="text-content-subtle">--</span> },
                {
                  key: 'budgeted_amount', header: 'Presupuestado', numeric: true,
                  sortValue: b => Number(b.budgeted_amount),
                  render: b => <span className="font-semibold whitespace-nowrap">{fmtCLP(b.budgeted_amount)}</span>,
                },
                {
                  key: 'periodo', header: 'Período',
                  render: b => (b.month ? `${MONTH_LABELS[b.month]} ${b.year}` : `Anual ${b.year}`),
                },
                {
                  key: 'acciones', header: 'Acciones',
                  render: b => <div className="text-right"><BudgetDeleteButton budget={b} /></div>,
                },
              ]}
              rows={budgets}
              rowKey={b => b.id}
              surface={false}
              minWidth={900}
              caption="Presupuestos"
            />
          )}
        </div>
      )}

      {/* Variance Table */}
      {viewMode === 'variance' && (
        <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-x-auto shadow-card">
          {loadingVariance ? (
            <div className="flex items-center justify-center py-16 text-content-subtle">
              <Loader2 size={20} className="animate-spin mr-2" />Cargando...
            </div>
          ) : variance.length === 0 ? (
            <EmptyState
              icon={TrendingUp}
              title="Sin datos de variacion para el periodo seleccionado"
              size="md"
            />
          ) : (
            <DataTable
              columns={[
                {
                  key: 'account_code', header: 'Cuenta',
                  render: v => (
                    <>
                      <span className="font-mono text-xs">{v.account_code}</span>
                      <span className="text-content-subtle ml-1 text-xs">{v.account_name}</span>
                    </>
                  ),
                },
                { key: 'cost_center_name', header: 'Centro Costo', render: v => v.cost_center_name || <span className="text-content-subtle">--</span> },
                { key: 'period', header: 'Período' },
                { key: 'budgeted', header: 'Presupuestado', numeric: true, sortValue: v => Number(v.budgeted), render: v => fmtCLP(v.budgeted) },
                { key: 'actual', header: 'Real', numeric: true, sortValue: v => Number(v.actual), render: v => fmtCLP(v.actual) },
                {
                  key: 'variance', header: 'Variacion', numeric: true,
                  sortValue: v => Number(v.variance),
                  render: v => (
                    <span className={`font-semibold whitespace-nowrap ${v.status === 'over' ? 'text-danger-content-soft' : 'text-success-content-soft'}`}>
                      {fmtCLP(v.variance)}
                    </span>
                  ),
                },
                {
                  key: 'variance_pct', header: '%', numeric: true,
                  sortValue: v => parseFloat(v.variance_pct),
                  render: v => {
                    const isOver = v.status === 'over'
                    const pct = parseFloat(v.variance_pct)
                    return (
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                        isOver ? 'bg-danger-surface text-danger-content' : 'bg-success-surface text-success-content'
                      }`}>
                        {isOver ? <TrendingDown size={10} /> : <TrendingUp size={10} />}
                        {isNaN(pct) ? '--' : `${pct.toFixed(1)}%`}
                      </span>
                    )
                  },
                },
              ]}
              rows={variance}
              rowKey={v => v.budget_id}
              surface={false}
              minWidth={980}
              caption="Variación entre presupuesto y real"
            />
          )}
        </div>
      )}

      {modalOpen && (
        <BudgetForm
          accounts={accounts}
          costCenters={costCenters}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  )
}
